import { useCallback, useEffect, useRef, useState } from "react";
import { FaArrowLeft, FaArrowRight, FaRobot, FaShieldAlt, FaUserTie, FaGraduationCap, FaCheckCircle } from "react-icons/fa";
import "./CyberSecSlider.css";

// ─────────────────────────────────────────────────────────────────────────────
// BACKGROUND IMAGES — one per slide
// Using Unsplash Source URLs (free, no API key, CC0 licensed).
// Each is fixed at 1600×900 so browsers can cache a single copy.
// The CSS overlay darkens them to keep ZU brand colours dominant.
// ─────────────────────────────────────────────────────────────────────────────
// Slide 1 — AI / robot / deepfake technology
const BG_INTRO       = "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1600&q=80&fit=crop";
// Slide 2 — university campus / openness
const BG_UNIVERSITY  = "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=1600&q=80&fit=crop";
// Slide 3 — business / staff authority
const BG_STAFF       = "https://images.unsplash.com/photo-1552664730-d307ca884978?w=1600&q=80&fit=crop";
// Slide 4 — students / job seeking
const BG_STUDENT     = "https://images.unsplash.com/photo-1523580494863-6f3031224c94?w=1600&q=80&fit=crop";
// Slide 5 — verification / action
const BG_VERIFICATION = "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=1600&q=80&fit=crop";

// How long each slide holds before auto-advancing (ms).
const SLIDE_DURATION = 7000;

// ─────────────────────────────────────────────────────────────────────────────
// WEEK 3 SLIDES  —  AI-Driven Threats and Deepfakes
// Each slide is a focused, actionable awareness card.
// ─────────────────────────────────────────────────────────────────────────────
const CYBER_WEEK3_SLIDES = [
  // ── Intro / Campaign Hook ──────────────────────────────────────────────────
  {
    id: "ai-intro",
    bgImage: BG_INTRO,
    week: "Week 3",
    theme: "Cybersecurity Awareness Month 2026",
    eyebrow: "AI-Driven Threats & Deepfakes",
    headline: "Your Instincts Are No Longer Enough",
    body: "The threat is no longer obvious. AI can generate perfect phishing emails, clone voices, and create deepfake videos. Learn how to protect yourself from AI-powered deception.",
    icon: FaRobot,
    accent: "shield",
    cta: { label: "Learn About AI Threats", href: "/cyber-security-awareness-week3" },
    stat: { value: "81.7%", label: "of students fell for AI-phishing in a study" },
  },

  // ── Slide 2: Why Universities Are Targets ────────────────────────────────
  {
    id: "ai-universities",
    bgImage: BG_UNIVERSITY,
    week: "Week 3 · The Target",
    theme: "Cybersecurity Awareness Month 2026",
    eyebrow: "Why Universities?",
    headline: "Openness Is Your Strength. Also Your Vulnerability.",
    body: "Universities are built on openness: public directories, published research, social media. AI exploits this transparency to craft highly personalized attacks.",
    icon: FaShieldAlt,
    accent: "lock",
    key: "The social engineering attempts that felt 'off' are now much harder to detect.",
    factors: [
      { factor: "Open research environments", desc: "Designed for collaboration, but vulnerable to data scraping" },
      { factor: "Diverse populations", desc: "Varying technical expertise across staff and students" },
      { factor: "AI at scale", desc: "Attackers deploy AI-powered deception at unprecedented scale" },
    ],
  },

  // ── Slide 3: Staff-Specific Risks ───────────────────────────────────────────
  {
    id: "ai-staff",
    bgImage: BG_STAFF,
    week: "Week 3 · Staff Risks",
    theme: "Cybersecurity Awareness Month 2026",
    eyebrow: "Staff-Specific Risks",
    headline: "Authority, Money, and Identity",
    body: "Deepfake voice cloning and video impersonation are being used to manipulate employees into transferring funds or granting access. AI can also generate fake student identities for fraud.",
    icon: FaUserTie,
    accent: "warning",
    risks: [
      { type: "Executive Impersonation", desc: "Deepfake voice/video used to approve fake payments or access" },
      { type: "Ghost Students", desc: "AI-generated synthetic identities for fraudulent enrollment and financial aid theft" },
      { type: "Spear-Phishing", desc: "AI scrapes public profiles to craft highly targeted messages referencing your real work" },
    ],
    takeaway: "Never approve money, access, or credential changes during a call — verify via a separate channel.",
  },

  // ── Slide 4: Student-Specific Risks ───────────────────────────────────────
  {
    id: "ai-students",
    bgImage: BG_STUDENT,
    week: "Week 3 · Student Risks",
    theme: "Cybersecurity Awareness Month 2026",
    eyebrow: "Student-Specific Risks",
    headline: "Jobs, Credentials, and Harassment",
    body: "Students face fake job offers, deepfake interviews, credential theft, and non-consensual deepfakes. Overconfidence is dangerous — verify everything.",
    icon: FaGraduationCap,
    accent: "mobile",
    risks: [
      { type: "Fake Job Offers", desc: "Remote internship offers asking for bank details or credentials before hiring" },
      { type: "Credential Theft", desc: "AI-crafted phishing targeting the valuable @zetech.ac.ke email" },
      { type: "Deepfake Harassment", desc: "Manipulated audio, images, and video used for reputational harm" },
      { type: "Overconfidence", desc: "Students overestimate their ability to detect AI attacks" },
    ],
    takeaway: "A job offer that pressures immediate action or asks for credentials upfront is suspicious — verify through official channels.",
  },

  // ── Slide 5: Verification Steps ────────────────────────────────────────────
  {
    id: "ai-verification",
    bgImage: BG_VERIFICATION,
    week: "Week 3 · Take Action",
    theme: "Cybersecurity Awareness Month 2026",
    eyebrow: "The Verification Rule",
    headline: "Pause. Disconnect. Verify. Report.",
    body: "Any urgent request involving money, credentials, access, or sensitive data must be verified through an independent, known channel. The request itself is never proof.",
    icon: FaCheckCircle,
    accent: "check",
    cta: { label: "Report to ICT", href: "mailto:itsupport@zetech.ac.ke?subject=AI%20Threat%20Report" },
    steps: [
      { step: "1. Pause", desc: "Do not act on urgency — it's the attacker's primary tool" },
      { step: "2. Disconnect", desc: "End the call, close the email, step away — break their control" },
      { step: "3. Verify", desc: "Use a known channel: directory number, in-person, official portal" },
      { step: "4. Report", desc: "Notify IT security or campus security — protect others" },
    ],
    warning: "Do not verify by replying to the email, calling provided numbers, or trusting caller ID.",
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

// Detect mobile viewport so we can limit the amount of content rendered
// inside the fixed-height hero. Content-heavy slides are trimmed on small screens.
function useMobileBreakpoint(px = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < px : false
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${px - 1}px)`);
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [px]);
  return isMobile;
}

export default function CyberSecWeek3Slider({ onCycleEnd, onRequestVideo }) {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const isMobile = useMobileBreakpoint(768);

  const onCycleEndRef = useRef(onCycleEnd);
  const onRequestVideoRef = useRef(onRequestVideo);
  useEffect(() => { onCycleEndRef.current = onCycleEnd; }, [onCycleEnd]);
  useEffect(() => { onRequestVideoRef.current = onRequestVideo; }, [onRequestVideo]);

  const hoverCapable = useRef(
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(hover: hover)").matches
  );

  const reducedMotion = useRef(
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );

  const nextSlide = useCallback(() => {
    setCurrent((prev) => (prev + 1) % CYBER_WEEK3_SLIDES.length);
  }, []);

  const prevSlide = useCallback(() => {
    if (current === 0 && onRequestVideoRef.current) {
      onRequestVideoRef.current();
      return;
    }
    setCurrent((prev) => (prev - 1 + CYBER_WEEK3_SLIDES.length) % CYBER_WEEK3_SLIDES.length);
  }, [current]);

  // Touch / swipe
  const touchStart = useRef(null);
  const handleTouchStart = (e) => { touchStart.current = e.touches?.[0]?.clientX ?? null; };
  const handleTouchEnd = (e) => {
    if (touchStart.current === null) return;
    const delta = (e.changedTouches?.[0]?.clientX ?? touchStart.current) - touchStart.current;
    touchStart.current = null;
    if (Math.abs(delta) < 45) return;
    if (delta < 0) nextSlide();
    else prevSlide();
  };

  // Keyboard
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "ArrowRight") nextSlide();
      if (e.key === "ArrowLeft") prevSlide();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [nextSlide, prevSlide]);

  // Auto-advance
  useEffect(() => {
    if (paused) return undefined;
    const timer = setInterval(() => {
      setCurrent((prev) => {
        if (prev >= CYBER_WEEK3_SLIDES.length - 1) return prev;
        return prev + 1;
      });
    }, SLIDE_DURATION);
    return () => clearInterval(timer);
  }, [paused]);

  // Finished last slide → hand off to HeroSlider
  useEffect(() => {
    if (paused || current < CYBER_WEEK3_SLIDES.length - 1) return undefined;
    const timer = setTimeout(() => {
      if (onCycleEndRef.current) onCycleEndRef.current();
    }, SLIDE_DURATION);
    return () => clearTimeout(timer);
  }, [current, paused]);

  const slide = CYBER_WEEK3_SLIDES[current];
  const Icon = slide.icon;

  return (
    <section
      className={`cyber-slider cyber-slider--${slide.accent}`}
      onMouseEnter={() => { if (hoverCapable.current) setPaused(true); }}
      onMouseLeave={() => { if (hoverCapable.current) setPaused(false); }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      aria-roledescription="carousel"
      aria-label="Cybersecurity Awareness Month 2026 — Week 3"
      data-reduced-motion={reducedMotion.current ? "true" : "false"}
    >
      {/* Background — full-bleed photo + brand overlay + circuit pattern */}
      <div className="cyber-bg" aria-hidden="true">
        {/* The photo — fades in on mount via CSS, Ken-Burns-free (still image) */}
        <img
          key={slide.bgImage}
          src={slide.bgImage}
          alt=""
          className="cyber-bg-photo"
          loading="eager"
          decoding="async"
          fetchPriority="high"
        />
        {/* Brand-tinted gradient scrim over the photo */}
        <div className="cyber-bg-scrim" />
        {/* Subtle SVG circuit overlay — keeps brand feel */}
        <div className="cyber-bg-grid" />
        {/* Accent colour radial glow */}
        <div className="cyber-glow cyber-glow--1" />
        <div className="cyber-glow cyber-glow--2" />
      </div>

      {/* Binary / hex rain strip — decorative */}
      <div className="cyber-rain" aria-hidden="true">
        {["01", "FF", "A3", "7E", "C1", "0D", "B9", "22", "5A", "F4"].map((v, i) => (
          <span key={i} className="cyber-rain__char" style={{ "--i": i }}>
            {v}
          </span>
        ))}
      </div>

      {/* Main content */}
      <div className="cyber-overlay">
        <div className="cyber-wrapper">

          {/* LEFT: campaign identity */}
          <div className="cyber-identity">
            <div className="cyber-shield-wrap" aria-hidden="true">
              <Icon className="cyber-icon" />
            </div>
            <p className="cyber-campaign">{slide.theme}</p>
            <span className="cyber-week-badge">{slide.week}</span>
          </div>

          {/* RIGHT: slide content */}
          <div className="cyber-content">
            <span className="cyber-eyebrow">{slide.eyebrow}</span>
            <h1 className="cyber-headline">{slide.headline}</h1>

            {slide.body && <p className="cyber-body">{slide.body}</p>}

            {/* University factors */}
            {slide.factors && (
              <ul className="cyber-factors">
                {(isMobile ? slide.factors.slice(0, 2) : slide.factors).map((f) => (
                  <li key={f.factor} className="cyber-factor-item">
                    <strong className="cyber-factor-type">{f.factor}</strong>
                    <span className="cyber-factor-desc">{f.desc}</span>
                  </li>
                ))}
              </ul>
            )}

            {/* Staff risks */}
            {slide.risks && (
              <ul className="cyber-risks">
                {(isMobile ? slide.risks.slice(0, 2) : slide.risks).map((r) => (
                  <li key={r.type} className="cyber-risk-item">
                    <strong className="cyber-risk-type">{r.type}</strong>
                    <span className="cyber-risk-desc">{r.desc}</span>
                  </li>
                ))}
              </ul>
            )}

            {/* Key takeaway */}
            {slide.key && (
              <div className="cyber-key">
                <span className="cyber-key-text">{slide.key}</span>
              </div>
            )}

            {/* Staff/student takeaway */}
            {slide.takeaway && (
              <div className="cyber-takeaway">
                <span className="cyber-takeaway-text">{slide.takeaway}</span>
              </div>
            )}

            {/* Verification steps */}
            {slide.steps && (
              <ol className="cyber-steps">
                {(isMobile ? slide.steps.slice(0, 2) : slide.steps).map((s) => (
                  <li key={s.step} className="cyber-step-item">
                    <strong className="cyber-step-num">{s.step}</strong>
                    <span className="cyber-step-desc">{s.desc}</span>
                  </li>
                ))}
              </ol>
            )}

            {/* Warning */}
            {slide.warning && (
              <div className="cyber-warning">
                <span className="cyber-warning-text">{slide.warning}</span>
              </div>
            )}

            {/* Stat callout */}
            {slide.stat && (
              <div className="cyber-stat">
                <span className="cyber-stat-value">{slide.stat.value}</span>
                <span className="cyber-stat-label">{slide.stat.label}</span>
              </div>
            )}

            {/* CTA */}
            {slide.cta && (
              <a
                href={slide.cta.href}
                className="cyber-cta"
                target={slide.cta.href.startsWith("http") ? "_blank" : undefined}
                rel={slide.cta.href.startsWith("http") ? "noopener noreferrer" : undefined}
              >
                {isMobile ? "Read Full Article →" : slide.cta.label}
              </a>
            )}
          </div>

        </div>
      </div>

      {/* Arrows */}
      <button
        className="hero-arrow1 hero-arrow-left1 cyber-arrow"
        onClick={prevSlide}
        aria-label="Previous slide"
      >
        <FaArrowLeft />
      </button>
      <button
        className="hero-arrow1 hero-arrow-right1 cyber-arrow"
        onClick={nextSlide}
        aria-label="Next slide"
      >
        <FaArrowRight />
      </button>

      {/* Indicators */}
      <div className="hero-indicators cyber-indicators" role="tablist" aria-label="Choose a cybersecurity awareness slide">
        {onRequestVideo && (
          <button
            type="button"
            className="indicator indicator--video"
            onClick={() => onRequestVideo()}
            aria-label="Play the intro video"
            title="Intro video"
          />
        )}
        {CYBER_WEEK3_SLIDES.map((s, i) => (
          <button
            key={s.id}
            type="button"
            role="tab"
            aria-selected={current === i}
            className={`indicator ${current === i ? "active" : ""}`}
            onClick={() => setCurrent(i)}
            aria-label={`Cybersecurity slide: ${s.eyebrow}`}
            title={s.eyebrow}
          >
            {current === i && !paused && (
              <span className="indicator-progress" key={`cp-${i}`} style={{ animationDuration: `${SLIDE_DURATION}ms` }} />
            )}
          </button>
        ))}
      </div>

      {/* Screen-reader status */}
      <p className="hero-sr-status" aria-live="polite">
        {`Cybersecurity slide ${current + 1} of ${CYBER_WEEK3_SLIDES.length}: ${slide.headline}`}
      </p>
    </section>
  );
}
